import React, { useEffect, useState } from "react";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  FlashMessage,
  Input,
  Modal,
  SelectNew,
  Radio,
  EditableText,
} from "../../components/common";
import candidateServices from "../../services/candidate.services";
import { updateBasicInformation } from "../../reducers/auth/session.reducer";
import { useForm } from "react-hook-form";
import { CUSTOM_GENDER_OPTIONS } from "../../utils/appConstants";
import { extractFirstLastName } from "../../utils/helper";

const isMissingGender = (u: any) => {
  const g = _.get(u, "gender", null);
  if (g === null || g === undefined) return true;
  const s = String(g).trim().toLowerCase();
  return s.length === 0 || s === "null";
};

type Props = {
  open: boolean;
  onClose: () => void;
  blocking?: boolean;
};

const SocialLoginRequireModal: React.FC<Props> = ({
  open,
  onClose,
  blocking = true,
}) => {
  const dispatch = useDispatch();
  const sessionUser = useSelector((s: any) => s?.session?.currentUser);
  const token = useSelector((s: any) => s?.session?.token);
  const [candidatePronoun, setCandidatePronoun] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    clearErrors,
  } = useForm({});

  // sync from store -> component
  useEffect(() => {
    const pronoun = _.get(sessionUser, "gender", "") || "";
    setCandidatePronoun(pronoun);
    if (sessionUser) {
      // show combined first + last name in the same editable field (like Basic Info modal)
      const first = _.get(sessionUser, "first_name", "") || "";
      const last = _.get(sessionUser, "last_name", "") || "";
      const fullName = `${first}${first && last ? " " : ""}${last}`.trim();
      setValue("first_name", fullName);
      // still set contact_info separately
      setValue("contact_info", _.get(sessionUser, "contact_info", "") || "");
    }
  }, [sessionUser, setValue]);

  useEffect(() => {
    if (!open) return;
    if (token && !isMissingGender(sessionUser)) onClose();
  }, [token, sessionUser, open, onClose]);

  if (!open) return null;

  const handleSave = async (formPayload: any) => {
    setLoading(true);
    const fullName = extractFirstLastName(formPayload.first_name);
    const firstName = fullName.firstName;
    const lastName = fullName.lastName;
    const formData = new FormData();

    formData.append("first_name", firstName);
    formData.append("last_name", lastName);
    formData.append("gender", candidatePronoun);
    formData.append(
      "gender_pronoun",
      _.get(formPayload, "gender_pronoun.value", "")
    );
    formData.append(
      "gender_optional",
      _.get(formPayload, "gender_optional", "")
    );
    formData.append("contact_info", _.get(formPayload, "contact_info", ""));

    try {
      const responseData = await candidateServices.updateSocialUser(formData);

      if (_.get(responseData, "flag") === true) {
        const updatedData = {
          first_name: firstName,
          last_name: lastName,
          gender: candidatePronoun,
          gender_pronoun: _.get(formPayload, "gender_pronoun.value", 0) || 0,
          gender_optional: _.get(formPayload, "gender_optional", ""),
          contact_info: _.get(formPayload, "contact_info", ""),
        };

        dispatch(updateBasicInformation(updatedData));
      } else {
        FlashMessage(
          _.get(responseData, "message", "Unable to update gender information"),
          "error"
        );
      }
    } catch (error) {
      FlashMessage(
        _.get(error, "message", "Unable to update gender information"),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    if (blocking || loading) return;
    onClose();
  };

  return (
    <Modal
      className="gender-required-modal"
      visible={open}
      onClose={handleModalClose}
      closeButton={false}
      title="Complete your profile"
    >
      <form
        onSubmit={(e) => {
          clearErrors();
          handleSubmit(handleSave)(e);
        }}
        className="edit-modal-wrapper"
      >
        <div>
          <div className="d-flex ">
            <div className="d-flex flex-col">
              <span className="profile-basic-lbl">Name</span>
              <div className="require-user-name">
                <EditableText
                  name="first_name"
                  inputClassName="editable-input"
                  control={control}
                  error={errors.first_name}
                  rules={{
                    required: { value: true, message: "Name is required" },
                    maxLength: {
                      value: 20,
                      message: "Name must be between 2 to 20 characters long",
                    },
                    minLength: {
                      value: 2,
                      message: "Name must be between 2 to 20 characters long",
                    },
                  }}
                />
              </div>
              <div className="d-flex flex-col mt-3">
                <span className="profile-basic-lbl">Gender</span>
                <div className="form-group gender-group">
                  <Radio
                    name="gender"
                    control={control}
                    wrapperClassName="gender-radio-wrapper w-100"
                    labelClassName="gender-radio"
                    validationObj={errors}
                    onChange={(e: React.ChangeEvent<any>) => {
                      const pronounce = _.get(e, "target.value", "");
                      setCandidatePronoun(pronounce);
                    }}
                    onBlur={(e) => {}}
                    options={[
                      { label: "Female", value: "female" },
                      { label: "They", value: "they" },
                      { label: "Custom", value: "custom" },
                    ]}
                    inputRef={register("gender", {
                      required: {
                        value: true,
                        message: "Please select gender",
                      },
                    })}
                  />
                </div>
              </div>
              <div className="d-flex">
                {candidatePronoun === "custom" && (
                  <div className="d-flex gap-15px pronoun-fields">
                    <div>
                      <SelectNew
                        name="gender_pronoun"
                        placeholder="Select your pronoun"
                        className="select-box"
                        options={CUSTOM_GENDER_OPTIONS}
                        isSearchable={false}
                        validationObj={errors}
                        control={control}
                        rules={{
                          required: {
                            value: true,
                            message: "Please select your pronoun",
                          },
                        }}
                      />
                    </div>
                    <div>
                      <Input
                        // name="gender_optional"
                        size="small"
                        placeholder="Customize your pronoun"
                        validationObj={errors}
                        {...register("gender_optional", {
                          required: {
                            value: true,
                            message: "Customize your pronoun",
                          },
                          maxLength: {
                            value: 15,
                            message: "Can't exceed more then 15 Character",
                          },
                        })}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="d-flex flex-col email-phone mt-1">
          <span className="profile-basic-lbl">Phone Number</span>
          <EditableText
            inputClassName="editable-input"
            control={control}
            name="contact_info"
            country="us"
            error={errors.contact_info}
            rules={{
              required: {
                value: true,
                message: "Phone is required",
              },
            }}
          />
        </div>
        {/* <div className="info-wrapper">
          <div className="mb-2">Gender is required to continue.</div>

          <div className="d-flex mt-3">
            <div className="form-group gender-group">
              <Radio
                name="gender"
                control={control}
                wrapperClassName="gender-radio-wrapper"
                labelClassName="gender-radio"
                validationObj={errors}
                onChange={(e: React.ChangeEvent<any>) => {
                  const pronounce = _.get(e, "target.value", "");
                  setCandidatePronoun(pronounce);
                }}
                onBlur={(e) => {}}
                options={[
                  { label: "Female", value: "female" },
                  { label: "They", value: "they" },
                  { label: "Custom", value: "custom" },
                ]}
                inputRef={register("gender", {
                  required: {
                    value: true,
                    message: "Please select gender",
                  },
                })}
              />
            </div>
          </div>

          <div className="d-flex">
            {candidatePronoun === "custom" && (
              <div className="d-flex gap-15px pronoun-fields">
                <div>
                  <SelectNew
                    name="gender_pronoun"
                    placeholder="Select your pronoun"
                    className="select-box"
                    options={CUSTOM_GENDER_OPTIONS}
                    isSearchable={false}
                    validationObj={errors}
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: "Please select your pronoun",
                      },
                    }}
                  />
                </div>
                <div>
                  <Input
                    size="small"
                    placeholder="Customize your pronoun"
                    validationObj={errors}
                    {...register("gender_optional", {
                      required: {
                        value: true,
                        message: "Customize your pronoun",
                      },
                      maxLength: {
                        value: 15,
                        message: "Can't exceed more then 15 Character",
                      },
                    })}
                  />
                </div>
              </div>
            )}
          </div>
        </div> */}

        <div className="d-flex justify-content-center mt-30">
          <Button
            type="submit"
            color="primary"
            className="submit-button text-uppercase"
            loading={loading}
          >
            Save & Continue
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default SocialLoginRequireModal;
